<template>
  <div id="login__container">
    <div id="login__elements">
      <div class="login__logo">
        <img src="/img/logo.png" alt="REALITY" />
      </div>
      <p v-if="needsShowErrorMessage" class="login__error_message">
        {{ errorMessage }}
      </p>
      <p
        class="login__caption__privacy_policy"
        v-html="$t(`login__caption__privacy_policy`)"
      ></p>
      <div class="login__main">
        <div class="login__buttons">
          <login-button service="apple" @click.native="fetchUserWebLogin(5)" />
          <login-button service="x" @click.native="fetchUserWebLogin(1)" />
          <login-button service="google" @click.native="fetchUserWebLogin(2)" />
          <login-button service="line" @click.native="fetchUserWebLogin(3)" />
        </div>
        <div class="login__notice">
          <p class="login__notice_title">ログインできない場合</p>
          <p class="login__notice_body">
            X（旧Twitter）などのアプリ内ブラウザからはログインできない場合があります。<br />
            ログインできない場合、以下のボタンでURLをコピーして、SafariやChromeで開き直してください。
          </p>
          <button class="login__copy_button" @click="copyLoginUrl">
            {{ copied ? 'コピーしました' : 'URLをコピー' }}
          </button>
        </div>
      </div>
      <footer>
        <small>© REALITY</small>
      </footer>
    </div>
  </div>
</template>

<script>
import API from '@/utils/API'
import i18n from '@/i18n'
import { LanguageQueryMixin } from '@/mixins/LanguageQuery'
import LoginButton from '@/components/Login/LoginButton.vue'
import { setHandoverString } from '@/utils/stringHandover'

const createErrorMessage = (error) => {
  switch (Number(error)) {
    case 1:
      return i18n.t('errorAuthenticateFailed1')
    case 2:
      return i18n.t('errorAuthenticateFailed2')
    case 3:
      return i18n.t('errorAccountNotFound')
    case 4:
      return i18n.t('errorValidAccountNotFound')
    case 5:
      return i18n.t('errorPermissionRequired')
    case 6:
      return i18n.t('errorMaintenance')
    case 7:
      return i18n.t('errorNotAvailableAgeAccount')
    default:
      return null
  }
}

export default {
  components: {
    LoginButton,
  },
  mixins: [LanguageQueryMixin],
  data: function () {
    return {
      authorizeUrl: null,
      error: null,
      copied: false,
    }
  },
  computed: {
    errorMessage: function () {
      return createErrorMessage(this.error)
    },
    needsShowErrorMessage: function () {
      return createErrorMessage(this.error) != null
    },
  },
  watch: {
    authorizeUrl(val) {
      if (val) {
        window.location = val
      }
    },
  },
  methods: {
    fetchUserWebLogin: async function (sns_kind) {
      if (this.$route.query) {
        // ログイン前に引き継ぎたいパラメータをfilterしてsession storageに保存
        const filtered = this.filterQuery(this.$route.query)
        setHandoverString('utm', JSON.stringify(filtered))
      } else {
        // ログイン前に引き継ぐパラメータがない場合は空でセット
        setHandoverString('utm', '')
      }

      const request = API.Request.streamerUserWebLogin(sns_kind)
      const response = await API.load(request)
      if (response.authorize_url) {
        this.authorizeUrl = response.authorize_url
      }
    },
    isLoggedIn: function () {
      return this.$store.getters['session/isLoggedIn']
    },
    redirectAlreadyLoggedIn: function () {
      const token = this.$store.state.session.token
      if (token) {
        this.$store.dispatch('session/setToken', token)
      }

      const redirectPath = sessionStorage.getItem('redirect_after_login')
      sessionStorage.removeItem('redirect_after_login')

      if (redirectPath && redirectPath.startsWith('/r/')) {
        window.location.href = redirectPath
        return
      }

      this.$router.push({ path: redirectPath || '/mypage/' }).catch(() => {})
    },
    copyLoginUrl: function () {
      navigator.clipboard.writeText('https://reality.app/login')
      this.copied = true
      setTimeout(() => {
        this.copied = false
      }, 2000)
    },
    filterQuery: function (query) {
      let filtered = {}
      // ログイン後に引き継ぎたいパラメータを指定
      const utmParams = [
        'utm_source', // Google AnalyticsのカスタムURLによる流入元計測用（明示的に引き継がないとvue-routerによる遷移の結果追跡できなくなってしまう）
        'utm_medium', // 同上
        'utm_campaign', // 同上
      ]
      for (const key of utmParams) {
        if (this.$route.query[key]) {
          filtered[key] = this.$route.query[key]
        }
      }
      return filtered
    },
  },
  created() {
    const error = this.$route.query.e
    if (error) {
      this.error = Number(error)
    }

    // redirect クエリパラメータがあれば sessionStorage に保存 (web-contents-v2)
    const redirect = this.$route.query.redirect
    if (redirect && typeof redirect === 'string') {
      sessionStorage.setItem('redirect_after_login', redirect)
    }

    if (this.isLoggedIn()) {
      this.redirectAlreadyLoggedIn()
    }
  },
}
</script>

<style scoped>
#login__container {
  width: 100%;
  min-height: 100%;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
}

#login__elements {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  max-width: 384px;
  padding: 64px 16px 80px;
}

.login__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 288px;
}

.login__logo img {
  width: 240px;
  height: 32px;
  object-fit: contain;
}

.login__error_message {
  width: 285px;
  font-size: 14px;
  text-align: center;
  color: #ff003d;
}

/deep/ p.login__caption__privacy_policy {
  font-size: 10px;
  font-weight: 400;
  line-height: 16px;
  color: #bababa;
  padding: 0 16px;
  text-align: center;
}

/deep/ p.login__caption__privacy_policy > a {
  color: #188dff;
  text-decoration: none;
}

.login__main {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
}

.login__buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.login__notice {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  max-width: 320px;
  padding: 16px;
  background-color: #f2f2f2;
  border-radius: 8px;
  text-align: center;
}

.login__notice_title {
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #0f0f0f;
  margin: 0;
}

.login__notice_body {
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  font-weight: 400;
  color: #666666;
  margin: 0;
}

footer {
  width: 100%;
  text-align: center;
}

footer small {
  font-size: 14px;
  color: #808080;
}

.login__copy_button {
  background-color: #188dff;
  color: #ffffff;
  border: none;
  border-radius: 999px;
  padding: 6px 16px;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  font-weight: 700;
  cursor: pointer;
}

.login__copy_button:hover {
  background-color: #0a6feb;
}

.login__copy_button:active {
  background-color: #0f59be;
}
</style>
